<template>
  <div class="countCard_Header d-flex justify-content-end content-center mb-4 mt-4">
    <button v-if="!isFree" @click="openModal" class="btn-overview btn-overview-cancel mr-3">
      {{ $t('planSubscription.overview.changeOrCancelPlan.cancelPlan') }}
    </button>
    <button v-if="isFree" @click="goToManagePlan" class="btn-overview btn-overview-change">
      {{ $t('planSubscription.overview.changeOrCancelPlan.upgradePlan') }}
    </button>
    <button v-if="!isFree" @click="goToManagePlan" class="btn-overview btn-overview-change">
      {{ $t('planSubscription.overview.changeOrCancelPlan.changePlan') }}
    </button>
    <CModal :show.sync="isOpen" @update:show="closeModal()" @keyup.esc="closeModal()" :no-close-on-backdrop="true">
      <template #header>
        <h6 v-show="step == 1">{{ $t('planSubscription.overview.changeOrCancelPlan.step1.header') }}</h6>
        <h5 v-show="step == 3">{{ $t('planSubscription.overview.changeOrCancelPlan.step3.header') }}</h5>
      </template>

      <div v-show="step == 1" class="mt-3 firstStep">
        <h6 class="mb-4">What made you cancel your plan? Check all that apply.</h6>
        <div class="ml-1" v-for="rs in reasonToCancel" :key="rs.key">
          <CInputCheckbox
            :key="rs.key"
            :label="rs.translated"
            class="checkboxTable mt-2 labelCheck"
            :checked.sync="rs.checked"
            custom
            @change="checkIsOneInputIsChecked"
          />
        </div>
        <br />
        <template v-if="isTextAreaOpen">
          <span><strong>Tell us more</strong> (optional)</span>
          <textarea v-model="otherReason" rows="5" class="form-control"></textarea>
        </template>
      </div>
      <div v-show="step == 2">
        <img src="@/assets/svg/Cancel1.svg" class="cancelPlanStep3" />
        <h4 class="mb-2">Do you really want to cancel?</h4>
        <div class="cancelSubTitle">If you cancel you'll loose {{ formatDate(nextInvoice) }}.</div>
        <div class="cancelSubTitle">This is an irreversible action.</div>
      </div>
      <div v-show="step == 3">
        <div class="textStep3 mb-3">
          If you cancel, you'll change to our free plan on {{ formatDate(nextInvoice) }}. This is an irreversible action. All items below will be
          reduced:
        </div>
        <div v-for="rd in reducedItems" :key="rd" class="itemStep3 mt-1">&bull; {{ rd }}</div>
        <div class="textStep3 mt-3">Still want to proceed with canceling your marketing automation account?</div>
      </div>
      <div v-show="step == 4">
        <img src="@/assets/svg/CancelSucefull.svg" class="cancelPlanStep3" />
        <h4 class="mb-2">Your plan has been successfully canceled!</h4>
        <div class="cancelSubTitle">You will lose access in {{ formatDate(nextInvoice) }}.</div>
        <div class="cancelSubTitle">Until then, you can continue using your plan.</div>
      </div>

      <template #footer>
        <div v-show="step == 1">
          <button @click="closeModal" class="btn-overview btn-overview-cancel mt-2 mr-2">Back to account</button>
          <button
            @click="goToStep(2)"
            class="btn-overview mt-2"
            :disabled="!anyReasonChecked"
            :class="{ 'btn-overview-change': anyReasonChecked == true, 'btn-overview-disabled': anyReasonChecked == false }"
          >
            Continue to cancel
          </button>
        </div>
        <div v-show="step == 2">
          <button @click="closeModal()" class="btn-overview btn-overview-cancel mt-2 mr-2">Back to account</button>
          <button @click="goToStep(3)" class="btn-overview btn-overview-change btn-step-2">Cancel Plan</button>
        </div>
        <div v-show="step == 3">
          <button @click="closeModal()" class="btn-overview btn-overview-cancel mr-2">Back to account</button>
          <button @click="cancelPlan()" :disabled="cancelLoading" class="btn-overview btn-overview-change btn-step-2">Cancel Plan</button>
        </div>
        <div v-show="step == 4">
          <button @click="confirmClose()" class="btn-overview btn-overview-change btn-step-4">Ok</button>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
import services from '@/services.js'
import { dateFormatString } from '@/utils/dateUtil'

let reasons = [
  {
    label: "No longer using Sendit's products",
    checked: false,
    key: 'noLongerUsingSendit',
  },
  {
    label: 'Projec is over or no longer active',
    checked: false,
    key: 'projectIsOver',
  },
  {
    label: 'Did not mean to upgrade',
    checked: false,
    key: 'didNotMeanUpgrade',
  },
  {
    label: 'Too expensive',
    checked: false,
    key: 'tooExpensive',
  },
  {
    label: 'Billing structure is unclear',
    checked: false,
    key: 'billingStructureUnclear',
  },
  {
    label: 'Switching to a different plan',
    checked: false,
    key: 'switchingToDifferentPlan',
  },
  {
    label: 'Other',
    checked: false,
    key: 'other',
    value: null,
  },
]

export default {
  name: 'OverViewChangeOrCancelPlan',
  data() {
    return {
      isOpen: false,
      isDisabled: true,
      cancelLoading: false,
      loading: false,
      isTextAreaOpen: false,
      otherReason: '',
      reasonTextArea: '',
      noLongerUsingSendit: false,
      step: 1,
      reasonToCancel: [],
      reducedItems: ['Platform Users', 'Sub accounts', 'Campaign templates', 'Automation flows', 'E-mail support'],
    }
  },
  props: {
    currentPlan: {
      type: Object,
      default: () => {},
    },
    isFree: {
      type: Boolean,
      default: false,
    },
    nextInvoice: {
      type: String,
      default: '',
    },
  },
  mounted() {
    this.getReasons()
  },
  computed: {
    anyReasonChecked: function () {
      return this.reasonToCancel.find((elm) => elm.checked) ? true : false
    },
  },
  methods: {
    openModal() {
      this.isOpen = true
    },

    formatDate(date) {
      return dateFormatString(date)
    },

    goToManagePlan() {
      this.$router.push({ name: 'manage-balance-plans' })
    },
    closeModal() {
      this.reasonToCancel.forEach((elm) => {
        elm.checked = false
      })
      this.otherReason = ''
      this.isTextAreaOpen = this.isOpen = false
      this.step = 1
    },

    confirmClose() {
      this.closeModal()
      // Força um reload da página
      window.logcation.reload(true)
    },

    checkIsOneInputIsChecked() {
      let reason = this.reasonToCancel.filter((item) => item.checked === true)
      let reasonOther = this.reasonToCancel.find((item) => item.label == 'OTHER')
      this.isDisabled = reason.length > 0 ? false : true
      this.isTextAreaOpen = reasonOther.checked ? true : false
    },
    goToStep(value) {
      this.step = value
    },
    getReasons() {
      this.loading = true
      services
        .getReasonsToCancel()
        .then((res) => {
          this.reasonToCancel = res.data.data?.map((elm) => {
            let result = {
              key: elm.id,
              label: elm.description,
              translated: this.$t(`planSubscription.overview.changeOrCancelPlan.reasons.${elm.description}`),
              checked: false,
            }
            return result
          })

          // ? Processo pra garantir que a opção 'Other' vai ficar no final
          let other = this.reasonToCancel.find((el) => el.label == 'OTHER')
          let index = this.reasonToCancel.indexOf(other)
          this.reasonToCancel.splice(index, 1)
          this.reasonToCancel.push(other)
        })
        .finally(() => (this.loading = false))
    },
    cancelPlan() {
      let organizationId = this.$route.params.organizationId
      let other = this.reasonToCancel.find((el) => el.label == 'OTHER')

      if (!other.checked) this.otherReason = ''

      let data = {
        additionalReason: this.otherReason,
        reasons: this.reasonToCancel.filter((elm) => elm.checked).map((elm) => elm.key),
      }
      this.cancelLoading = true
      services
        .cancelOrganizationPlan(organizationId, data)
        .then((res) => {
          this.goToStep(4)
        })
        .catch(({ response }) => {
          this.isOpen = false
          this.$emit('error', {
            status: 'danger',
            text: response.data.message || 'Erro',
          })
        })
        .finally(() => (this.cancelLoading = false))
    },
  },
}
</script>

<style lang="scss" scope>
.currentPlan {
  border-radius: 10px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.currentPlanColOne {
  border-right: 1px solid #dee2e6;
  text-align: center;
  padding: 50px 20px;
}

.currentPlanColOne h6 {
  font-size: 0.9rem;
  line-height: 140px;
}

.checkMark {
  color: $primary;
}

.blockFeatures {
  color: #343a40;
  font-size: 14px;
}

.currentPlanColThree {
  text-align: center;
}

.btn-overview {
  padding: 10px 30px;
  font-size: 14px;
  border-radius: 5px;
}

.btn-overview-cancel {
  border: 1px solid #dee2e6;
  background-color: #fff;
  color: #343a40;
}

.btn-overview-increase {
  border: 2px solid $primary;
  background-color: #fff;
  color: $primary;
  font-weight: bold;
}

.btn-overview-change {
  border: 1px solid $primary;
  background-color: $primary;
  color: #fff;
}

.btn-overview-disabled {
  border: 1px solid #ced4da;
  background-color: #ced4da;
  color: #fff;
}

.firstStep {
  text-align: left;
}

.labelCheck {
  line-height: 2;
  font-weight: 600;
  font-size: 14px;
}

.cancelPlanStep3 {
  height: 370px;
}

.cancelSubTitle {
  color: #868e96;
  font-size: 16px;
}

.btn-step-2 {
  margin-right: 65px;
}

.btn-step-4 {
  margin-right: 140px;
  min-width: 150px;
}

.textStep3 {
  text-align: left;
  font-size: 14px;
}
.itemStep3 {
  text-align: left;
  font-size: 14px;
  margin-left: 15px;
}
</style>
